/**
 * BlueNoiseRuntime — singleton blue-noise texture initialization.
 *
 * Generates a tiling 2D blue-noise LUT once at startup via the void-and-
 * cluster algorithm and uploads it as a `THREE.DataTexture` configured
 * for proper sampling (RepeatWrapping + NearestFilter, NoColorSpace).
 *
 * Default size 64² RGBA8 = 16 KB on GPU. Generation cost: ~100 ms.
 *
 * Mirrors the `ltcRuntime.ts` pattern: idempotent init, module-level
 * singleton, optional reset for tests.
 */

import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';
import { Logger } from '../../core/Logger.js';
import { generateBlueNoiseRGBA } from './BlueNoiseGenerator.js';
import { BakeCache } from '../../platform/BakeCache.js';

/** Bump when the void-and-cluster algorithm changes (cached tiles are then ignored). */
export const BLUE_NOISE_BAKE_VERSION = 1;

let _initialized = false;
let _texture: THREE.DataTexture | null = null;
let _size = 0;
const _frameUniform = uniform(0);

export interface BlueNoiseInitOptions {
  /** Texture side length. Default 64. Larger = less repetition, more memory. */
  size?: number;
  /** PRNG seed for reproducibility. Default 0xCAFE. */
  seed?: number;
}

/**
 * Initialize the blue-noise runtime. Idempotent — subsequent calls are
 * no-ops. Returns true on success.
 */
export function initBlueNoise(opts: BlueNoiseInitOptions = {}): boolean {
  if (_initialized) return true;
  const size = opts.size ?? 64;
  const seed = opts.seed ?? 0xcafe;
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());

  try {
    const data = generateBlueNoiseRGBA(size, seed);
    _install(data, size);
    const dt = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
    Logger.info('BlueNoise',
      `Generated ${size}×${size} RGBA8 blue-noise LUT in ${dt.toFixed(0)}ms`);
    return true;
  } catch (err) {
    Logger.warn('BlueNoise', `init failed: ${err}`);
    return false;
  }
}

/** The canonical texture for a tile's bytes (fresh bake or cache-restored — identical parameters). */
function _install(data: Uint8Array, size: number): void {
  const tex = new THREE.DataTexture(data, size, size);
  tex.format = THREE.RGBAFormat;
  tex.type = THREE.UnsignedByteType;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.NoColorSpace;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  tex.name = 'BlueNoise';
  _texture = tex;
  _size = size;
  _initialized = true;
}

export function blueNoiseCacheKey(size: number, seed: number): string {
  return `blue-noise:${size}:${seed >>> 0}`;
}

/** Start the cache read now (module construction) so a late init finds the tile waiting. */
export function prefetchBlueNoiseCache(opts: BlueNoiseInitOptions = {}, cache: BakeCache = BakeCache.shared()): void {
  cache.prefetch(blueNoiseCacheKey(opts.size ?? 64, opts.seed ?? 0xcafe), BLUE_NOISE_BAKE_VERSION);
}

/**
 * Same as `initBlueNoise()` but restores the tile from the BakeCache when a
 * previous session generated it (byte-identical), else generates + stores.
 * Idempotent; never throws — cache trouble degrades to the plain bake.
 */
export async function initBlueNoiseCached(opts: BlueNoiseInitOptions = {}, cache: BakeCache = BakeCache.shared()): Promise<boolean> {
  if (_initialized) return true;
  const size = opts.size ?? 64;
  const seed = opts.seed ?? 0xcafe;
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  try {
    const hit = await cache.get(blueNoiseCacheKey(size, seed), BLUE_NOISE_BAKE_VERSION);
    if (_initialized) return true; // a sync init won the race while we awaited
    if (hit && hit.length === 1 && hit[0].byteLength === size * size * 4) {
      _install(hit[0], size);
      const dt = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
      Logger.info('BlueNoise', `Restored ${size}×${size} RGBA8 blue-noise LUT from cache in ${dt.toFixed(0)}ms`);
      return true;
    }
  } catch { /* fall through to the bake */ }
  const ok = initBlueNoise({ size, seed });
  if (ok && _texture) void cache.put(blueNoiseCacheKey(size, seed), BLUE_NOISE_BAKE_VERSION, [_texture.image.data as Uint8Array]);
  return ok;
}

/** Returns the singleton texture or null if not initialized. */
export function getBlueNoiseTexture(): THREE.DataTexture | null {
  return _texture;
}

/** Side length of the loaded texture (0 if not initialized). */
export function getBlueNoiseSize(): number {
  return _size;
}

/** True if `initBlueNoise()` has been called successfully. */
export function isBlueNoiseInitialized(): boolean {
  return _initialized;
}

/**
 * Frame-counter uniform exposed for TSL accessors. Update each frame from
 * the engine's main tick.
 */
export function getBlueNoiseFrameUniform(): ReturnType<typeof uniform> {
  return _frameUniform;
}

/**
 * Set the frame counter (modulo 65536 to avoid float precision drift).
 * The TSL accessors use this to advance the noise pattern via golden-
 * ratio offset for temporal stratification.
 */
export function setBlueNoiseFrame(frame: number): void {
  _frameUniform.value = frame % 65536;
}

/** Test-only: reset state. */
export function _resetBlueNoise(): void {
  if (_texture) _texture.dispose();
  _texture = null;
  _size = 0;
  _initialized = false;
  _frameUniform.value = 0;
}
